/**
 * `SceneSpec` — kontrakt calej aplikacji (docs/SCENESPEC.md).
 *
 * Wersjonowany dokument JSON walidowany Zodem. Canvas go renderuje, LLM go
 * patchuje, kompilator promptu zamienia w prompt, a rewizje sa zapisywane w
 * `scene_revisions`.
 *
 * Zasady tego pliku:
 *  - nazwy pol i wartosci enumow sa DOKLADNIE takie jak w dokumentacji —
 *    golden testy i kompilator promptu na tym stoja,
 *  - wszystkie obiekty sa `strictObject`: nieznany klucz to blad walidacji.
 *    Dzieki temu `applyScenePatch` odrzuca sciezki nieistniejace w schemacie
 *    takze wtedy, gdy operacja `add` probuje dopisac nowe pole,
 *  - katy sa calkowite (`z.int()`), zgodnie z konwencja z dokumentacji,
 *  - zero DOM, zero `cloudflare:*` — plik dziala w przegladarce i w Workerze.
 */

import { z } from 'zod';
import { ID_SEPARATOR, ID_RANDOM_LENGTH, isId, type Id, type IdPrefix, type PresetId, type ProductId, type ProductImageId } from '../ids';

/** Wersja schematu dokumentu. Podbicie wymaga migracji w `migrate.ts`. */
export const SCENE_SPEC_VERSION = 2;

/** Typ literalny wersji — uzywany w `z.literal`. */
export type SceneSpecVersion = 2;

// ─────────────────────────────────────────────────────────────────────────────
// Pomocnicze schematy skalarne
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Brandowany identyfikator jako schemat Zoda.
 *
 * W runtime to zwykly `ZodString` z regexem — dzieki temu generator JSON Schema
 * (dla system promptu asystenta) i walker sciezek w `patch.ts` widza `string`,
 * a staticznie dostajemy `Id<P>` zamiast golego `string`.
 */
function brandedIdSchema<P extends IdPrefix>(prefix: P): z.ZodType<Id<P>, string> {
  const pattern = new RegExp(`^${prefix}${ID_SEPARATOR}[A-Za-z0-9_-]{${ID_RANDOM_LENGTH}}$`);
  return z
    .string()
    .regex(pattern, `expected id with prefix "${prefix}${ID_SEPARATOR}"`)
    .refine((value): value is Id<P> => isId(prefix, value));
}

const ProductIdSchema: z.ZodType<ProductId, string> = brandedIdSchema('prd');
const ProductImageIdSchema: z.ZodType<ProductImageId, string> = brandedIdSchema('pim');
const PresetIdSchema: z.ZodType<PresetId, string> = brandedIdSchema('pst');

/** Kolor w formacie `#RRGGBB`. Wielkosc liter dowolna, hash liczy sie z bajtow. */
export const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
export const HexColorSchema = z.string().regex(HEX_COLOR_PATTERN, 'expected #RRGGBB color');

/** Wspolrzedna w przestrzeni kadru: `0..1`, origin lewy-gorny. */
export const UnitSchema = z.number().min(0).max(1);

/** Frakcja `0..1` (softness, grain, opacity, ...). */
export const FractionSchema = z.number().min(0).max(1);

/** Procent `0..100`. */
export const PercentSchema = z.number().min(0).max(100);

/**
 * Lokalny identyfikator wezla / swiatla / pedestalu — unikalny w obrebie specu.
 * Bez `/` i `~`, zeby dal sie bezpiecznie uzyc w JSON Pointerze.
 */
export const NODE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
export const LocalIdSchema = z.string().regex(NODE_ID_PATTERN, 'expected local id [A-Za-z0-9_-]');

/**
 * Lokalny identyfikator jako typ. Swiadomie ALIAS `string`, nie brand — id
 * wezla przychodzi z patcha LLM i z JSON Pointera, wiec kazde brandowanie
 * konczyloby sie rzutowaniem w kazdym miejscu uzycia. Unikalnosc pilnuje
 * niezmiennik `duplicate_node_id`, a format — `NODE_ID_PATTERN`.
 */
export type LocalId = string;

// ─────────────────────────────────────────────────────────────────────────────
// Enumy (kolejnosc wartosci dokladnie jak w docs/SCENESPEC.md)
// ─────────────────────────────────────────────────────────────────────────────

export const VERTICALS = ['fashion', 'pharma'] as const;
export const VerticalSchema = z.enum(VERTICALS);
export type VerticalId = (typeof VERTICALS)[number];

export const SCENE_PURPOSES = [
  'ecommerce_pdp',
  'hero_banner',
  'social_square',
  'print_catalog',
  'amazon_main',
  'lifestyle',
] as const;
export const ScenePurposeSchema = z.enum(SCENE_PURPOSES);
export type ScenePurpose = (typeof SCENE_PURPOSES)[number];

export const CANVAS_ASPECTS = ['1:1', '4:5', '3:2', '2:3', '16:9', '9:16', '4:3', '5:4'] as const;
export const CanvasAspectSchema = z.enum(CANVAS_ASPECTS);
export type CanvasAspect = (typeof CANVAS_ASPECTS)[number];

export const TARGET_RESOLUTIONS = ['1K', '2K', '4K'] as const;
export const TargetResolutionSchema = z.enum(TARGET_RESOLUTIONS);
export type TargetResolution = (typeof TARGET_RESOLUTIONS)[number];

export const CANVAS_BACKGROUNDS = ['auto', 'transparent', 'opaque'] as const;
export const CanvasBackgroundSchema = z.enum(CANVAS_BACKGROUNDS);
export type CanvasBackground = (typeof CANVAS_BACKGROUNDS)[number];

export const SHOT_TYPES = [
  'packshot_isolated',
  'still_life',
  'lifestyle',
  'macro_detail',
  'flat_lay',
  'on_body',
] as const;
export const ShotTypeSchema = z.enum(SHOT_TYPES);
export type ShotType = (typeof SHOT_TYPES)[number];

export const CAMERA_ANGLE_NAMES = [
  'eye_level',
  'three_quarter_high',
  'low_hero',
  'top_down',
  'worms_eye',
  'dutch',
] as const;
export const CameraAngleNameSchema = z.enum(CAMERA_ANGLE_NAMES);
export type CameraAngleName = (typeof CAMERA_ANGLE_NAMES)[number];

export const CAMERA_HEIGHTS = ['floor', 'below_product', 'product_center', 'above_product'] as const;
export const CameraHeightSchema = z.enum(CAMERA_HEIGHTS);
export type CameraHeightRelative = (typeof CAMERA_HEIGHTS)[number];

export const FOCAL_LENGTHS_MM = [24, 35, 50, 85, 100, 135, 180] as const;
export const FocalLengthSchema = z.union([
  z.literal(24),
  z.literal(35),
  z.literal(50),
  z.literal(85),
  z.literal(100),
  z.literal(135),
  z.literal(180),
]);
export type FocalLengthMm = (typeof FOCAL_LENGTHS_MM)[number];

export const DEPTH_OF_FIELDS = ['deep_all_sharp', 'shallow_product_sharp', 'macro_razor'] as const;
export const DepthOfFieldSchema = z.enum(DEPTH_OF_FIELDS);
export type DepthOfField = (typeof DEPTH_OF_FIELDS)[number];

export const COMPOSITION_RULES = [
  'center',
  'thirds',
  'golden',
  'diagonal',
  'symmetry',
  'negative_space_left',
  'negative_space_right',
] as const;
export const CompositionRuleSchema = z.enum(COMPOSITION_RULES);
export type CompositionRule = (typeof COMPOSITION_RULES)[number];

export const PRODUCT_PLACEMENTS = [
  'grounded',
  'floating',
  'leaning',
  'tilted_hero',
  'stacked',
  'embedded_in_hand',
] as const;
export const ProductPlacementSchema = z.enum(PRODUCT_PLACEMENTS);
export type ProductPlacement = (typeof PRODUCT_PLACEMENTS)[number];

export const PRODUCT_ROLES = ['hero', 'secondary', 'context'] as const;
export const ProductRoleSchema = z.enum(PRODUCT_ROLES);
export type ProductRole = (typeof PRODUCT_ROLES)[number];

export const PROP_RELATIONS = ['behind', 'in_front', 'beside', 'under', 'inside'] as const;
export const PropRelationSchema = z.enum(PROP_RELATIONS);
export type PropRelation = (typeof PROP_RELATIONS)[number];

export const TEXT_ZONE_PURPOSES = ['copy_space', 'logo_space'] as const;
export const TextZonePurposeSchema = z.enum(TEXT_ZONE_PURPOSES);
export type TextZonePurpose = (typeof TEXT_ZONE_PURPOSES)[number];

/**
 * TODO(spec): SCENESPEC.md odwoluje sie do typu `SurfaceMaterial`, ale nie
 * wylicza jego wartosci. Lista ponizej jest wyprowadzona z `surfacePreset`
 * obu vertykali (VERTICALS.md) i uporzadkowana leksykograficznie. Czy ma byc
 * zamknietym enumem, czy wolnym stringiem z presetami?
 */
export const SURFACE_MATERIALS = [
  'bamboo',
  'brushed_steel',
  'frosted_glass',
  'glass',
  'linen',
  'marble',
  'matte_acrylic',
  'oak_plank',
  'raw_concrete',
  'sand',
  'seamless_paper',
  'slate',
  'terrazzo',
  'travertine',
  'water',
] as const;
export const SurfaceMaterialSchema = z.enum(SURFACE_MATERIALS);
export type SurfaceMaterial = (typeof SURFACE_MATERIALS)[number];

export const SURFACE_FINISHES = ['matte', 'satin', 'polished', 'wet'] as const;
export const SurfaceFinishSchema = z.enum(SURFACE_FINISHES);
export type SurfaceFinish = (typeof SURFACE_FINISHES)[number];

export const BACKDROP_KINDS = [
  'seamless',
  'gradient',
  'wall',
  'environment',
  'sky',
  'studio_cyc',
  'transparent',
] as const;
export const BackdropKindSchema = z.enum(BACKDROP_KINDS);
export type BackdropKind = (typeof BACKDROP_KINDS)[number];

/**
 * TODO(spec): ksztalty pedestalow nie sa wyliczone w SCENESPEC.md
 * (jest tylko `{id, shape, material, w/h/d mm, x, y, zIndex}`).
 */
export const PEDESTAL_SHAPES = ['cube', 'cylinder', 'slab', 'riser', 'step', 'sphere', 'arch'] as const;
export const PedestalShapeSchema = z.enum(PEDESTAL_SHAPES);
export type PedestalShape = (typeof PEDESTAL_SHAPES)[number];

export const TALENT_PRESENCES = [
  'none',
  'hands_only',
  'legs_feet',
  'torso_crop',
  'half_body',
  'full_body',
  'back_view',
] as const;
export const TalentPresenceSchema = z.enum(TALENT_PRESENCES);
export type TalentPresence = (typeof TALENT_PRESENCES)[number];

export const TALENT_INTERACTIONS = [
  'holding',
  'wearing',
  'carrying_shoulder',
  'crossbody',
  'presenting_palm',
  'pouring',
  'none',
] as const;
export const TalentInteractionSchema = z.enum(TALENT_INTERACTIONS);
export type TalentInteraction = (typeof TALENT_INTERACTIONS)[number];

export const TALENT_BODY_PARTS = [
  'left_hand',
  'right_hand',
  'both_hands',
  'left_foot',
  'right_foot',
  'shoulder',
  'wrist',
] as const;
export const TalentBodyPartSchema = z.enum(TALENT_BODY_PARTS);
export type TalentBodyPart = (typeof TALENT_BODY_PARTS)[number];

export const WARDROBE_TONALITIES = [
  'neutral_tonal',
  'monochrome_black',
  'denim',
  'editorial',
  'clinical_white',
  'athleisure',
] as const;
export const WardrobeTonalitySchema = z.enum(WARDROBE_TONALITIES);
export type WardrobeTonality = (typeof WARDROBE_TONALITIES)[number];

export const LIGHTING_RIGS = [
  'single_softbox',
  'clamshell',
  'three_point',
  'strip_edge',
  'hard_sun',
  'window_natural',
  'ring_macro',
  'gradient_wall',
] as const;
export const LightingRigSchema = z.enum(LIGHTING_RIGS);
export type LightingRig = (typeof LIGHTING_RIGS)[number];

export const LIGHT_ROLES = ['key', 'fill', 'rim', 'background', 'practical', 'kicker'] as const;
export const LightRoleSchema = z.enum(LIGHT_ROLES);
export type LightRole = (typeof LIGHT_ROLES)[number];

export const LIGHT_DISTANCES = ['close', 'mid', 'far'] as const;
export const LightDistanceSchema = z.enum(LIGHT_DISTANCES);
export type LightDistance = (typeof LIGHT_DISTANCES)[number];

export const LIGHT_MODIFIERS = [
  'softbox',
  'strip',
  'beauty_dish',
  'bare',
  'scrim',
  'bounce',
  'snoot',
  'grid',
] as const;
export const LightModifierSchema = z.enum(LIGHT_MODIFIERS);
export type LightModifier = (typeof LIGHT_MODIFIERS)[number];

export const LIGHTING_CONTRASTS = ['flat', 'soft', 'balanced', 'punchy', 'dramatic'] as const;
export const LightingContrastSchema = z.enum(LIGHTING_CONTRASTS);
export type LightingContrast = (typeof LIGHTING_CONTRASTS)[number];

export const SHADOW_KINDS = [
  'none',
  'soft_contact',
  'hard_directional',
  'cast_long',
  'gobo_pattern',
] as const;
export const ShadowKindSchema = z.enum(SHADOW_KINDS);
export type ShadowKind = (typeof SHADOW_KINDS)[number];

export const STRIP_HIGHLIGHTS = ['none', 'single_broad', 'dual_edge', 'wrap'] as const;
export const StripHighlightSchema = z.enum(STRIP_HIGHLIGHTS);
export type StripHighlight = (typeof STRIP_HIGHLIGHTS)[number];

export const STYLE_GRADES = [
  'neutral',
  'warm_film',
  'cool_clinical',
  'high_key',
  'low_key',
  'pastel',
  'contrasty_editorial',
] as const;
export const StyleGradeSchema = z.enum(STYLE_GRADES);
export type StyleGrade = (typeof STYLE_GRADES)[number];

export const RETOUCH_LEVELS = ['raw', 'catalog_clean', 'beauty_retouch'] as const;
export const RetouchLevelSchema = z.enum(RETOUCH_LEVELS);
export type RetouchLevel = (typeof RETOUCH_LEVELS)[number];

export const PREVIEW_STRATEGIES = ['batch_n9', 'fanout9', 'inherit'] as const;
export const PreviewStrategySchema = z.enum(PREVIEW_STRATEGIES);
export type PreviewStrategy = (typeof PREVIEW_STRATEGIES)[number];

export const VARIATION_AXES = ['lighting', 'angle', 'background', 'composition', 'mixed'] as const;
export const VariationAxisSchema = z.enum(VARIATION_AXES);
export type VariationAxis = (typeof VARIATION_AXES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Wezly kadru
// ─────────────────────────────────────────────────────────────────────────────

/** Skala wezla: frakcja szerokosci kadru. */
export const NodeScaleSchema = z.number().gt(0).max(5);

export const ProductNodeSchema = z.strictObject({
  kind: z.literal('product'),
  id: LocalIdSchema,
  productId: ProductIdSchema,
  productImageId: ProductImageIdSchema.optional(),
  x: UnitSchema,
  y: UnitSchema,
  scale: NodeScaleSchema,
  rotationDeg: z.int().min(-180).max(180),
  zIndex: z.int().min(-1000).max(1000),
  placement: ProductPlacementSchema,
  floatHeightPct: PercentSchema.optional(),
  mirrored: z.boolean().optional(),
  occludedBy: z.array(LocalIdSchema).optional(),
  role: ProductRoleSchema,
});
export type ProductNode = z.infer<typeof ProductNodeSchema>;

/**
 * SCENESPEC.md zapisuje props jako `{ id, presetId? | label: string, ... }`.
 * Oba pola sa opcjonalne w schemacie, a wymog "co najmniej jedno" jest
 * sprawdzany w `sceneSpecInvariants` — inaczej `discriminatedUnion` po `kind`
 * przestalby dzialac.
 */
export const PropNodeSchema = z.strictObject({
  kind: z.literal('prop'),
  id: LocalIdSchema,
  presetId: PresetIdSchema.optional(),
  label: z.string().min(1).max(200).optional(),
  x: UnitSchema,
  y: UnitSchema,
  scale: NodeScaleSchema,
  rotationDeg: z.int().min(-180).max(180),
  zIndex: z.int().min(-1000).max(1000),
  material: SurfaceMaterialSchema.optional(),
  relation: PropRelationSchema,
});
export type PropNode = z.infer<typeof PropNodeSchema>;

export const TalentNodeSchema = z.strictObject({
  kind: z.literal('talent'),
  id: LocalIdSchema,
  x: UnitSchema,
  y: UnitSchema,
  scale: NodeScaleSchema,
  zIndex: z.int().min(-1000).max(1000),
  cropTop: UnitSchema.optional(),
  cropBottom: UnitSchema.optional(),
});
export type TalentNode = z.infer<typeof TalentNodeSchema>;

export const TextZoneNodeSchema = z.strictObject({
  kind: z.literal('text_zone'),
  id: LocalIdSchema,
  x: UnitSchema,
  y: UnitSchema,
  w: z.number().gt(0).max(1),
  h: z.number().gt(0).max(1),
  purpose: TextZonePurposeSchema,
});
export type TextZoneNode = z.infer<typeof TextZoneNodeSchema>;

export const SceneNodeSchema = z.discriminatedUnion('kind', [
  ProductNodeSchema,
  PropNodeSchema,
  TalentNodeSchema,
  TextZoneNodeSchema,
]);
export type SceneNode = z.infer<typeof SceneNodeSchema>;
export type SceneNodeKind = SceneNode['kind'];

export const SCENE_NODE_KINDS = ['product', 'prop', 'talent', 'text_zone'] as const;

export const LightNodeSchema = z.strictObject({
  id: LocalIdSchema,
  role: LightRoleSchema,
  azimuthDeg: z.int().min(-180).max(180),
  elevationDeg: z.int().min(-90).max(90),
  distance: LightDistanceSchema,
  /** 0 = twarde, 1 = bardzo miekkie. */
  softness: FractionSchema,
  intensityPct: PercentSchema,
  colorTempK: z.int().min(2700).max(7500),
  modifier: LightModifierSchema,
});
export type LightNode = z.infer<typeof LightNodeSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Sekcje dokumentu
// ─────────────────────────────────────────────────────────────────────────────

export const SceneIntentSchema = z.strictObject({
  purpose: ScenePurposeSchema,
  deliverableNotes: z.string().max(2000).optional(),
});
export type SceneIntent = z.infer<typeof SceneIntentSchema>;

export const SceneCanvasSchema = z.strictObject({
  aspect: CanvasAspectSchema,
  targetResolution: TargetResolutionSchema,
  /** 0..25 — rysowane jako guide I emitowane jako constraint. */
  safeMarginPct: z.number().min(0).max(25),
  background: CanvasBackgroundSchema,
});
export type SceneCanvas = z.infer<typeof SceneCanvasSchema>;

export const CameraLensSchema = z.strictObject({
  focalMm: FocalLengthSchema,
  aperture: z.number().min(1.2).max(16),
});
export type CameraLens = z.infer<typeof CameraLensSchema>;

export const SceneCameraSchema = z.strictObject({
  shotType: ShotTypeSchema,
  angleName: CameraAngleNameSchema,
  /** Stopnie nad plaszczyzna pozioma produktu: -20..90. */
  elevationDeg: z.int().min(-20).max(90),
  /** Orbita wokol produktu: -180..180, 0 = os kamery. */
  azimuthDeg: z.int().min(-180).max(180),
  /** Roll kamery: -15..15. */
  tiltDeg: z.int().min(-15).max(15),
  heightRelative: CameraHeightSchema,
  lens: CameraLensSchema,
  depthOfField: DepthOfFieldSchema,
  /** Id wezla albo `'product:label'`. */
  focusTarget: z.string().min(1).max(120),
  /** Packshot bez keystone. */
  perspectiveCorrection: z.boolean(),
});
export type SceneCamera = z.infer<typeof SceneCameraSchema>;

export const SceneCompositionSchema = z.strictObject({
  rule: CompositionRuleSchema,
  /** 5..90 — ile kadru zajmuje produkt. */
  productCoveragePct: z.number().min(5).max(90),
  /** Produkt celowo wychodzi za krawedz. */
  cropBleed: z.boolean(),
});
export type SceneComposition = z.infer<typeof SceneCompositionSchema>;

export const SceneFrameSchema = z.strictObject({
  /** Uporzadkowane; `zIndex` autorytatywny. */
  nodes: z.array(SceneNodeSchema).max(64),
  composition: SceneCompositionSchema,
});
export type SceneFrame = z.infer<typeof SceneFrameSchema>;

export const SetSurfaceSchema = z.strictObject({
  material: SurfaceMaterialSchema,
  finish: SurfaceFinishSchema,
  colorHex: HexColorSchema,
  seamlessSweep: z.boolean(),
  /**
   * Glebokosc sweepu jako procent odleglosci od kamery do horyzontu.
   * `100` = pelny sweep (krawedz blatu niewidoczna, linia podlogi pokrywa sie
   * z horyzontem), `0` = blat konczy sie u dolnej krawedzi kadru.
   *
   * Pole istnieje, bo `floorLineY` i `floorGuides` w `geometry.ts` przyjmuja
   * `sweepDepthPct` — bez niego linia podlogi i horyzontu byly tym samym i
   * przyciaganie "do podlogi" nie roznilo sie od "do horyzontu", czyli nie dalo
   * sie postawic produktu na powierzchni.
   *
   * TODO(spec): PROMPTING.md nie wymienia tego pola w zadnej z 12 sekcji —
   * dzisiaj jest wylacznie danymi composera i NIE wchodzi do tekstu promptu.
   */
  sweepDepthPct: z.number().min(0).max(100),
});
export type SetSurface = z.infer<typeof SetSurfaceSchema>;

export const BackdropGradientSchema = z.strictObject({
  fromHex: HexColorSchema,
  toHex: HexColorSchema,
  angleDeg: z.int().min(-180).max(180),
});
export type BackdropGradient = z.infer<typeof BackdropGradientSchema>;

export const SetBackdropSchema = z.strictObject({
  kind: BackdropKindSchema,
  colorHex: HexColorSchema.optional(),
  gradient: BackdropGradientSchema.optional(),
  environmentRefPresetId: PresetIdSchema.optional(),
  blurPct: PercentSchema,
});
export type SetBackdrop = z.infer<typeof SetBackdropSchema>;

export const PedestalSchema = z.strictObject({
  id: LocalIdSchema,
  shape: PedestalShapeSchema,
  material: SurfaceMaterialSchema,
  wMm: z.number().gt(0).max(10000),
  hMm: z.number().gt(0).max(10000),
  dMm: z.number().gt(0).max(10000),
  x: UnitSchema,
  y: UnitSchema,
  zIndex: z.int().min(-1000).max(1000),
});
export type Pedestal = z.infer<typeof PedestalSchema>;

export const AtmosphereSchema = z.strictObject({
  haze: FractionSchema,
  dust: FractionSchema,
  smoke: FractionSchema,
  waterMist: FractionSchema,
  floatingParticles: z.boolean(),
});
export type Atmosphere = z.infer<typeof AtmosphereSchema>;

export const SceneSetSchema = z.strictObject({
  surface: SetSurfaceSchema,
  backdrop: SetBackdropSchema,
  pedestals: z.array(PedestalSchema).max(16),
  atmosphere: AtmosphereSchema,
});
export type SceneSet = z.infer<typeof SceneSetSchema>;

export const TalentWardrobeSchema = z.strictObject({
  tonality: WardrobeTonalitySchema,
  notes: z.string().max(500).optional(),
});
export type TalentWardrobe = z.infer<typeof TalentWardrobeSchema>;

export const TalentGroomingSchema = z.strictObject({
  nails: z.string().max(120).optional(),
  jewelry: z.boolean(),
  watch: z.boolean(),
});
export type TalentGrooming = z.infer<typeof TalentGroomingSchema>;

export const SceneTalentSchema = z.strictObject({
  presence: TalentPresenceSchema,
  /** ZAWSZE preferuj referencje obrazowa nad opis tekstowy osoby. */
  refPresetId: PresetIdSchema.optional(),
  /** Slownik poz z pakietu vertykalnego. */
  pose: z.string().max(200),
  interaction: TalentInteractionSchema,
  bodyPart: TalentBodyPartSchema.optional(),
  wardrobe: TalentWardrobeSchema,
  grooming: TalentGroomingSchema,
  faceVisible: z.boolean(),
  /** Rekord zgody / praw do wizerunku. */
  consentRefId: z.string().max(200).optional(),
});
export type SceneTalent = z.infer<typeof SceneTalentSchema>;

export const LightingShadowSchema = z.strictObject({
  kind: ShadowKindSchema,
  opacity: FractionSchema,
  directionDeg: z.int().min(-180).max(180),
  /** Dlugosc cienia jako procent wysokosci kadru. */
  lengthPct: PercentSchema,
});
export type LightingShadow = z.infer<typeof LightingShadowSchema>;

export const LightingReflectionsSchema = z.strictObject({
  showFloorReflection: z.boolean(),
  specularControlPct: PercentSchema,
  stripHighlight: StripHighlightSchema,
});
export type LightingReflections = z.infer<typeof LightingReflectionsSchema>;

export const SceneLightingSchema = z.strictObject({
  rig: LightingRigSchema,
  lights: z.array(LightNodeSchema).max(12),
  /** key:fill, 1..8. */
  ratio: z.number().min(1).max(8),
  colorTempK: z.int().min(2700).max(7500),
  contrast: LightingContrastSchema,
  shadow: LightingShadowSchema,
  reflections: LightingReflectionsSchema,
});
export type SceneLighting = z.infer<typeof SceneLightingSchema>;

export const StylePaletteSchema = z.strictObject({
  primaryHex: HexColorSchema,
  secondaryHex: HexColorSchema,
  accentHex: HexColorSchema,
  neutralHex: HexColorSchema,
});
export type StylePalette = z.infer<typeof StylePaletteSchema>;

export const SceneStyleSchema = z.strictObject({
  palette: StylePaletteSchema,
  /** np. ['minimal','premium','clinical','warm','editorial'] */
  mood: z.array(z.string().min(1).max(60)).max(12),
  referenceStylePresetIds: z.array(PresetIdSchema).max(12),
  grade: StyleGradeSchema,
  grain: FractionSchema,
  vignette: FractionSchema,
  chromaticAberration: FractionSchema,
  retouchLevel: RetouchLevelSchema,
});
export type SceneStyle = z.infer<typeof SceneStyleSchema>;

/**
 * `verticalParams` — dyskryminowana unia po polu `vertical`.
 *
 * Ksztalt parametrow zyje w pakietach vertykalnych (docs/VERTICALS.md):
 *  - `src/shared/verticals/fashion-params.ts` -> `FashionParamsSchema`,
 *  - `src/shared/verticals/pharma-params.ts`  -> `PharmaParamsSchema` (+ bramka
 *    `dosageDepiction`, ktora dzieki temu obowiazuje TAKZE dla requestu do API:
 *    kazda walidacja `SceneSpec` przechodzi przez ten schemat).
 *
 * Oba schematy to `strictObject`, wiec nieznany klucz w `verticalParams` jest
 * bledem walidacji, a `applyScenePatch` odrzuca sciezki poza schematem.
 *
 * Importujemy z plikow `*-params.ts`, a NIE z `verticals/{fashion,pharma}.ts`,
 * bo tamte importuja `verticals/registry.ts`, ktore importuje WARTOSC `VERTICALS`
 * z tego pliku — powstalby cykl `spec -> fashion -> registry -> spec` padajacy w
 * ESM na TDZ. Pliki `*-params.ts` sa liscami (tylko `zod`).
 */
export {
  FashionParamsSchema,
  type FashionParams,
} from '../verticals/fashion-params';
export {
  PharmaParamsSchema,
  type PharmaParams,
} from '../verticals/pharma-params';

import { FashionParamsSchema } from '../verticals/fashion-params';
import { PharmaParamsSchema } from '../verticals/pharma-params';

export const VerticalParamsSchema = z.discriminatedUnion('vertical', [
  FashionParamsSchema,
  PharmaParamsSchema,
]);
export type VerticalParams = z.infer<typeof VerticalParamsSchema>;

export const SceneConstraintsSchema = z.strictObject({
  /** Auto-wyprowadzone z parametrow twardych + dopiski uzytkownika. */
  hardText: z.array(z.string().min(1).max(1000)).max(64),
  /** Frazowanie negatywne. */
  forbid: z.array(z.string().min(1).max(200)).max(128),
  /** np. ['product label','outsole tread'] */
  mustKeepSharp: z.array(z.string().min(1).max(200)).max(32),
  noTextInScene: z.boolean(),
});
export type SceneConstraints = z.infer<typeof SceneConstraintsSchema>;

export const ModelOverrideSchema = z.strictObject({
  preview: z.string().min(1).max(200).optional(),
  master: z.string().min(1).max(200).optional(),
});
export type ModelOverride = z.infer<typeof ModelOverrideSchema>;

export const SceneGenerationSchema = z.strictObject({
  seed: z.int().min(0).max(4294967295).optional(),
  lockSeedAcrossTiers: z.boolean(),
  previewStrategy: PreviewStrategySchema,
  modelOverride: ModelOverrideSchema.optional(),
  variationAxis: VariationAxisSchema.optional(),
});
export type SceneGeneration = z.infer<typeof SceneGenerationSchema>;

export const SceneMetaSchema = z.strictObject({
  notes: z.string().max(4000).optional(),
  lastAssistantInstruction: z.string().max(2000).optional(),
});
export type SceneMeta = z.infer<typeof SceneMetaSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Dokument
// ─────────────────────────────────────────────────────────────────────────────

export const SceneSpecSchema = z.strictObject({
  version: z.literal(SCENE_SPEC_VERSION),
  vertical: VerticalSchema,
  intent: SceneIntentSchema,
  canvas: SceneCanvasSchema,
  camera: SceneCameraSchema,
  frame: SceneFrameSchema,
  set: SceneSetSchema,
  talent: SceneTalentSchema.optional(),
  lighting: SceneLightingSchema,
  style: SceneStyleSchema,
  verticalParams: VerticalParamsSchema,
  constraints: SceneConstraintsSchema,
  generation: SceneGenerationSchema,
  meta: SceneMetaSchema,
});

/** Kanoniczny typ dokumentu sceny. */
export type SceneSpec = z.infer<typeof SceneSpecSchema>;

/** Kolejnosc kluczy najwyzszego poziomu — informacyjna; hash sortuje sam. */
export const SCENE_SPEC_TOP_LEVEL_KEYS = [
  'canvas',
  'camera',
  'constraints',
  'frame',
  'generation',
  'intent',
  'lighting',
  'meta',
  'set',
  'style',
  'talent',
  'version',
  'vertical',
  'verticalParams',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Niezmienniki miedzypolowe
// ─────────────────────────────────────────────────────────────────────────────

export type SceneSpecInvariantCode =
  | 'vertical_params_mismatch'
  | 'prop_needs_preset_or_label'
  | 'duplicate_node_id'
  | 'duplicate_light_id'
  | 'duplicate_pedestal_id'
  | 'unknown_occluder'
  | 'talent_node_without_talent'
  | 'talent_node_presence_none'
  | 'gradient_backdrop_without_gradient'
  | 'text_zone_out_of_frame';

export type SceneSpecInvariantIssue = {
  /** JSON Pointer do pola, ktore lamie niezmiennik. */
  readonly path: string;
  readonly code: SceneSpecInvariantCode;
  readonly message: string;
};

/**
 * Reguly, ktorych nie da sie wyrazic ksztaltem schematu.
 *
 * Deterministyczne: iteracja po tablicach w kolejnosci dokumentu, bez
 * `Object.keys` bez sortowania, bez `Date` i `Math.random`.
 */
export function sceneSpecInvariants(spec: SceneSpec): readonly SceneSpecInvariantIssue[] {
  const issues: SceneSpecInvariantIssue[] = [];

  if (spec.verticalParams.vertical !== spec.vertical) {
    issues.push({
      path: '/verticalParams/vertical',
      code: 'vertical_params_mismatch',
      message: `verticalParams.vertical "${spec.verticalParams.vertical}" must equal vertical "${spec.vertical}"`,
    });
  }

  const nodeIds = new Set<string>();
  spec.frame.nodes.forEach((node, index) => {
    if (nodeIds.has(node.id)) {
      issues.push({
        path: `/frame/nodes/${index}/id`,
        code: 'duplicate_node_id',
        message: `duplicate node id "${node.id}"`,
      });
    }
    nodeIds.add(node.id);

    if (node.kind === 'prop' && node.presetId === undefined && node.label === undefined) {
      issues.push({
        path: `/frame/nodes/${index}`,
        code: 'prop_needs_preset_or_label',
        message: 'prop node requires presetId or label',
      });
    }

    if (node.kind === 'text_zone' && (node.x + node.w > 1 || node.y + node.h > 1)) {
      issues.push({
        path: `/frame/nodes/${index}`,
        code: 'text_zone_out_of_frame',
        message: 'text_zone must fit inside the frame (x + w <= 1, y + h <= 1)',
      });
    }

    if (node.kind === 'talent') {
      if (spec.talent === undefined) {
        issues.push({
          path: `/frame/nodes/${index}`,
          code: 'talent_node_without_talent',
          message: 'talent node requires spec.talent',
        });
      } else if (spec.talent.presence === 'none') {
        issues.push({
          path: `/frame/nodes/${index}`,
          code: 'talent_node_presence_none',
          message: 'talent node requires talent.presence != "none"',
        });
      }
    }
  });

  spec.frame.nodes.forEach((node, index) => {
    if (node.kind !== 'product' || node.occludedBy === undefined) return;
    node.occludedBy.forEach((occluderId, occluderIndex) => {
      if (!nodeIds.has(occluderId)) {
        issues.push({
          path: `/frame/nodes/${index}/occludedBy/${occluderIndex}`,
          code: 'unknown_occluder',
          message: `occludedBy references unknown node id "${occluderId}"`,
        });
      }
    });
  });

  const lightIds = new Set<string>();
  spec.lighting.lights.forEach((light, index) => {
    if (lightIds.has(light.id)) {
      issues.push({
        path: `/lighting/lights/${index}/id`,
        code: 'duplicate_light_id',
        message: `duplicate light id "${light.id}"`,
      });
    }
    lightIds.add(light.id);
  });

  const pedestalIds = new Set<string>();
  spec.set.pedestals.forEach((pedestal, index) => {
    if (pedestalIds.has(pedestal.id)) {
      issues.push({
        path: `/set/pedestals/${index}/id`,
        code: 'duplicate_pedestal_id',
        message: `duplicate pedestal id "${pedestal.id}"`,
      });
    }
    pedestalIds.add(pedestal.id);
  });

  if (spec.set.backdrop.kind === 'gradient' && spec.set.backdrop.gradient === undefined) {
    issues.push({
      path: '/set/backdrop/gradient',
      code: 'gradient_backdrop_without_gradient',
      message: 'backdrop.kind "gradient" requires backdrop.gradient',
    });
  }

  return issues;
}

// ─────────────────────────────────────────────────────────────────────────────
// Walidacja
// ─────────────────────────────────────────────────────────────────────────────

/** Znormalizowany problem walidacji — wspolny dla Zoda i niezmiennikow. */
export type SceneSpecIssue = {
  /** JSON Pointer, np. `/lighting/lights/0/azimuthDeg`. */
  readonly path: string;
  readonly code: string;
  readonly message: string;
};

export type SceneSpecValidationError = {
  readonly kind: 'scene_spec_invalid';
  readonly issues: readonly SceneSpecIssue[];
};

/** Zamiana sciezki Zoda na JSON Pointer. */
export function zodPathToPointer(path: ReadonlyArray<PropertyKey>): string {
  if (path.length === 0) return '';
  return path
    .map((segment) => `/${String(segment).replace(/~/g, '~0').replace(/\//g, '~1')}`)
    .join('');
}

/** Walidacja schematem + niezmienniki. Nie rzuca — zwraca liste problemow. */
export function validateSceneSpec(
  value: unknown,
): { readonly ok: true; readonly spec: SceneSpec } | { readonly ok: false; readonly error: SceneSpecValidationError } {
  const parsed = SceneSpecSchema.safeParse(value);
  if (!parsed.success) {
    return {
      ok: false,
      error: {
        kind: 'scene_spec_invalid',
        issues: parsed.error.issues.map((issue) => ({
          path: zodPathToPointer(issue.path),
          code: issue.code,
          message: issue.message,
        })),
      },
    };
  }

  const invariantIssues = sceneSpecInvariants(parsed.data);
  if (invariantIssues.length > 0) {
    return {
      ok: false,
      error: {
        kind: 'scene_spec_invalid',
        issues: invariantIssues.map((issue) => ({
          path: issue.path,
          code: issue.code,
          message: issue.message,
        })),
      },
    };
  }

  return { ok: true, spec: parsed.data };
}

/** Wariant rzucajacy — dla miejsc, gdzie niewalidny spec to blad programisty. */
export function parseSceneSpec(value: unknown): SceneSpec {
  const result = validateSceneSpec(value);
  if (!result.ok) {
    const first = result.error.issues[0];
    throw new TypeError(
      `Invalid SceneSpec: ${result.error.issues.length} issue(s), first at "${first?.path ?? ''}": ${first?.message ?? 'unknown'}`,
    );
  }
  return result.spec;
}

/** Typeguard bez informacji o bledach. */
export function isSceneSpec(value: unknown): value is SceneSpec {
  return validateSceneSpec(value).ok;
}
